import { Component, ReactNode } from 'react';
import { createUUID } from '@mews/std';
import { Positioner } from '@optimus-web/helpers';
import { IconName } from '@optimus-web/icons';
import { Button } from '../Button';
import { TooltipProps } from '../Tooltip';
import { CalendarService } from '../utils/services';
import CalendarDay from './CalendarDay';
import { MonthButtonContainerElement, MonthElement, WeekElement, WeeksContainerElement } from './style';
import { Modifiers } from './types';

export interface CalendarMonthProps<D, M, Y> {
    endDate?: D | null;
    focusedDate: D;
    getCalendarService: () => CalendarService<D, M, Y>;
    month: M;
    monthSelectedIndex: number;
    monthSelectorVisible: boolean;
    numberOfMonths: number;
    onDateClick: (event: React.MouseEvent, date: D) => void;
    allowDisabledClick?: boolean;
    enableAdjacentMonthSelection?: boolean;
    areSurroundingDaysOfSingleMonthHidden?: boolean;
    isSingleDaySelectionCircle?: boolean;
    modifiers?: Modifiers<D>;
    onMonthNameClick?: (month: M) => void;
    startDate?: D;
    startDateWithTooltip?: TooltipProps;
    dayWithTooltipRef?: React.Ref<Positioner>;
    getDateTooltip?: (day: D) => ReactNode | undefined;
    tooltipDelay?: number;
    isMobile?: boolean;
}

interface CalendarMonthState<D> {
    weeks: (D | null)[][];
    dayUUIDs: string[];
    weekUUIDs: string[];
}

export default class Month<D, M, Y> extends Component<CalendarMonthProps<D, M, Y>, CalendarMonthState<D>> {
    constructor(props: CalendarMonthProps<D, M, Y>) {
        super(props);
        const { month, getCalendarService } = props;
        const weeksOfTheMonth = getCalendarService().getWeeksOfMonth(month);

        this.state = {
            weeks: weeksOfTheMonth,
            dayUUIDs: Array(weeksOfTheMonth.reduce((currentCount, row) => currentCount + row.length, 0))
                .fill(0)
                .map(() => createUUID()),
            weekUUIDs: Array(weeksOfTheMonth.length)
                .fill(0)
                .map(() => createUUID()),
        };
    }

    handleMonthNameClick = () => {
        const { month, onMonthNameClick } = this.props;
        if (onMonthNameClick) {
            onMonthNameClick(month);
        }
    };

    isOutOfMonth = (date: D) => {
        const month = this.props.getCalendarService().getMonthOfDate(date);
        return !this.props.getCalendarService().isMonthEqual(month, this.props.month);
    };

    isLastDayOfTheMonth = (date: D) => {
        const { getCalendarService } = this.props;
        const nextDate = getCalendarService().getNextDate(date);
        const nextDateMonth = getCalendarService().getMonthOfDate(nextDate);
        const dateMonth = getCalendarService().getMonthOfDate(date);

        return !getCalendarService().isMonthEqual(dateMonth, nextDateMonth);
    };

    isFirstDayOfTheMonth = (date: D) => {
        const { getCalendarService } = this.props;
        const prevDate = getCalendarService().getPrevDate(date);
        const prevDateMonth = getCalendarService().getMonthOfDate(prevDate);
        const dateMonth = getCalendarService().getMonthOfDate(date);

        return !getCalendarService().isMonthEqual(dateMonth, prevDateMonth);
    };

    render() {
        const {
            endDate,
            focusedDate,
            month,
            getCalendarService,
            modifiers,
            monthSelectorVisible,
            monthSelectedIndex,
            // ignore
            onMonthNameClick,
            startDate,
            startDateWithTooltip,
            dayWithTooltipRef,
            getDateTooltip,
            isMobile,
            ...other
        } = this.props;
        const { weekUUIDs, dayUUIDs, weeks } = this.state;

        const monthName = getCalendarService().getMonthName(month);

        const finalModifiers: Modifiers<D> = {
            ...modifiers,
            isOutOfMonth: this.isOutOfMonth,
            isLastDayOfTheMonth: this.isLastDayOfTheMonth,
            isFirstDayOfTheMonth: this.isFirstDayOfTheMonth,
        };

        const monthSelectorExpanded =
            monthSelectorVisible && getCalendarService().getMonthIndex(month) === monthSelectedIndex;

        return (
            <MonthElement isMobile={isMobile}>
                <MonthButtonContainerElement monthSelectorExpanded={monthSelectorExpanded}>
                    <Button
                        onClick={this.handleMonthNameClick}
                        variant="ghost"
                        icon={monthSelectorExpanded ? IconName.ChevronUp : IconName.ChevronDown}
                        iconPosition="right"
                        size="small"
                        aria-expanded={monthSelectorExpanded}
                        aria-haspopup="true"
                        data-test-button-month-selector
                        type="submit"
                    >
                        {monthName}
                    </Button>
                </MonthButtonContainerElement>
                <WeeksContainerElement isMobile={isMobile}>
                    {weeks &&
                        weeks.map((week, weekIndex) => (
                            <WeekElement key={weekUUIDs[weekIndex]}>
                                {week &&
                                    week.map(
                                        (day, dayIndex) =>
                                            day && (
                                                <CalendarDay
                                                    key={dayUUIDs[dayIndex]}
                                                    day={day}
                                                    endDate={endDate}
                                                    getCalendarService={getCalendarService}
                                                    modifiers={finalModifiers}
                                                    focusedDate={focusedDate}
                                                    monthName={monthName}
                                                    startDate={startDate}
                                                    startDateWithTooltip={startDateWithTooltip}
                                                    dayWithTooltipRef={dayWithTooltipRef}
                                                    getDateTooltip={getDateTooltip}
                                                    isMobile={isMobile}
                                                    {...other}
                                                />
                                            )
                                    )}
                            </WeekElement>
                        ))}
                </WeeksContainerElement>
            </MonthElement>
        );
    }
}
